import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles, CheckCircle2, Lightbulb, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type Technique = 'zero-shot' | 'few-shot' | 'chain-of-thought';

const TECHNIQUES: Array<{
  id: Technique;
  label: string;
  description: string;
  starter: string;
  tip: string;
}> = [
  {
    id: 'zero-shot',
    label: 'Zero-shot',
    description: 'Ask the model to do something with no examples.',
    starter:
      'Classify the sentiment of this customer review as positive, neutral, or negative:\n\n"The delivery was late but the product is excellent."',
    tip: 'Be specific about the output format you want.',
  },
  {
    id: 'few-shot',
    label: 'Few-shot',
    description: 'Give the model 2–3 examples before the real question.',
    starter:
      'Translate English to formal French.\n\nEnglish: Good morning -> French: Bonjour\nEnglish: How are you? -> French: Comment allez-vous ?\nEnglish: Where is the train station? -> French:',
    tip: 'Examples teach the model the exact pattern you want.',
  },
  {
    id: 'chain-of-thought',
    label: 'Chain-of-thought',
    description: 'Tell the model to reason step by step.',
    starter:
      'A store sells pens at 1.20€ each and notebooks at 3.50€ each. A customer buys 4 pens and 3 notebooks. Think step by step, then give the final total.',
    tip: 'Phrases like "think step by step" unlock multi-step reasoning.',
  },
];

interface PromptPlaygroundProps {
  onAllCompleted: () => void;
}

export const PromptPlayground: React.FC<PromptPlaygroundProps> = ({ onAllCompleted }) => {
  const [active, setActive] = useState<Technique>('zero-shot');
  const [prompt, setPrompt] = useState<string>(TECHNIQUES[0].starter);
  const [reply, setReply] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [tried, setTried] = useState<Set<Technique>>(new Set());

  const switchTechnique = (id: Technique) => {
    const t = TECHNIQUES.find((x) => x.id === id)!;
    setActive(id);
    setPrompt(t.starter);
    setReply('');
  };

  const run = async () => {
    if (!prompt.trim()) {
      toast.error('Write a prompt first.');
      return;
    }
    setLoading(true);
    setReply('');
    try {
      const { data, error } = await supabase.functions.invoke('prompt-playground', {
        body: { prompt, technique: active },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      const text = (data as any)?.reply ?? '';
      setReply(text);

      const next = new Set(tried);
      if (!next.has(active)) {
        next.add(active);
        setTried(next);
        toast.success(`${TECHNIQUES.find((t) => t.id === active)!.label} technique tried!`);
        if (next.size === TECHNIQUES.length) {
          onAllCompleted();
        }
      }
    } catch (e: any) {
      const msg = e?.message || 'Something went wrong calling the AI.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const progress = tried.size;
  const allDone = progress === TECHNIQUES.length;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Interactive Prompt Playground</h3>
          <Badge variant="secondary" className="ml-auto">
            {progress} / {TECHNIQUES.length} techniques tried
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Try each of the three prompt-engineering techniques against a live model.
          Run each at least once to complete this lesson.
        </p>
      </div>

      {/* Technique tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TECHNIQUES.map((t) => {
          const done = tried.has(t.id);
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => switchTechnique(t.id)}
              className={`text-left rounded-lg border p-3 transition-tech min-h-[88px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                isActive
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/40'
              }`}
              aria-pressed={isActive}
              aria-label={`Select ${t.label} technique${done ? ', already tried' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm">{t.label}</span>
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden />
                ) : (
                  <Badge variant="outline" className="text-[10px]">Try me</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t.description}</p>
            </button>
          );
        })}
      </div>

      {/* Tip */}
      <div className="flex items-start gap-2 text-sm text-muted-foreground">
        <Lightbulb className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
        <span>{TECHNIQUES.find((t) => t.id === active)!.tip}</span>
      </div>

      {/* Editor */}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="playground-prompt">
          Your prompt
        </label>
        <Textarea
          id="playground-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="min-h-[140px] font-mono text-sm"
          maxLength={4000}
          placeholder="Write your prompt here..."
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{prompt.length} / 4000</span>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" /> Run prompt
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Output */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">AI response</span>
            <Badge variant="outline" className="text-[10px]">Lovable AI · Gemini 2.5 Flash</Badge>
          </div>
          {reply ? (
            <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">
              {reply}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              The model's response will appear here.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Status */}
      <div
        className={`rounded-lg border p-3 text-sm flex items-center gap-2 ${
          allDone
            ? 'border-green-600/40 bg-green-600/10 text-green-700 dark:text-green-400'
            : 'border-border bg-muted/40 text-muted-foreground'
        }`}
        aria-live="polite"
      >
        {allDone ? (
          <>
            <CheckCircle2 className="h-4 w-4" />
            All three techniques tried — you can complete this lesson now.
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" />
            Try the remaining {TECHNIQUES.length - progress} technique
            {TECHNIQUES.length - progress > 1 ? 's' : ''} to unlock lesson completion.
          </>
        )}
      </div>
    </div>
  );
};
